import React from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Package, ScanLine, Users, ReceiptText,
  Wallet, UserCog, CalendarCheck, CreditCard, LogOut, Banknote, Tag, BarChart3, Settings as SettingsIcon, Truck
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { useStores } from "../lib/stores";
import { imageUrl } from "../lib/api";
import { storeForTheme } from "../lib/storeThemes";
import logoSrc from "../assets/logo.png";

const nav = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/pos", label: "POS Billing", icon: ScanLine, testid: "nav-pos" },
  { to: "/app/sales", label: "Sales", icon: ReceiptText, testid: "nav-sales" },
  { to: "/app/collections", label: "Collections", icon: Banknote, testid: "nav-collections" },
  { to: "/app/products", label: "Products", icon: Package, testid: "nav-products" },
  { to: "/app/purchases", label: "Purchases", icon: Truck, testid: "nav-purchases" },
  { to: "/app/categories", label: "Categories", icon: Tag, testid: "nav-categories" },
  { to: "/app/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/app/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/app/expenses", label: "Expenses", icon: Wallet, testid: "nav-expenses" },
  { to: "/app/staff", label: "Staff", icon: UserCog, testid: "nav-staff" },
  { to: "/app/attendance", label: "Attendance", icon: CalendarCheck, testid: "nav-attendance" },
  { to: "/app/subscription", label: "Subscription", icon: CreditCard, testid: "nav-subscription" },
];

export default function Sidebar() {
  const { tenant, user, logout } = useAuth();
  const { stores, activeStoreId, setActive } = useStores();
  const nav_ = useNavigate();
  const location = useLocation();
  const settingsActive = location.pathname.startsWith("/app/settings");
  const activeStore = storeForTheme(stores, activeStoreId);
  const displayStore = activeStore || tenant?.default_store || null;
  const sidebarTitle = displayStore?.name || "VastraKiran";
  const storeImage = displayStore?.image;
  return (
    <aside className="w-64 shrink-0 hidden lg:flex flex-col border-r bg-white" style={{ borderColor: "var(--border-soft)" }}>
      <div className="px-5 py-6 border-b" style={{ borderColor: "var(--border-soft)" }}>
        <div className="flex items-center gap-2">
          <div
            className="w-10 h-10 rounded-md flex items-center justify-center overflow-hidden shrink-0"
            style={{
              background: storeImage ? "var(--surface-muted)" : "transparent",
              border: storeImage ? "1px solid var(--border-soft)" : "none",
            }}
            data-testid="sidebar-store-image"
          >
            {storeImage ? (
              <img src={imageUrl(storeImage)} alt="" className="w-full h-full object-cover" />
            ) : (
              <img src={logoSrc} alt="" className="w-full h-full object-contain p-0.5" />
            )}
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold text-lg truncate" data-testid="sidebar-store-name">{sidebarTitle}</div>
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>{tenant?.subscription?.status === "trial" ? "Trial" : (tenant?.subscription?.plan || "starter")} plan</div>
          </div>
        </div>
        {stores.length > 0 && (
          <div className="mt-4">
            <label className="text-xs font-medium" style={{ color: "var(--text-muted)" }}>Active store</label>
            <select
              data-testid="sidebar-store-switcher"
              value={activeStoreId}
              onChange={(e) => setActive(e.target.value)}
              className="mt-1 w-full h-10 px-2 rounded-md border bg-white text-sm"
              style={{ borderColor: "var(--border-soft)" }}
            >
              <option value="">All stores</option>
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {nav.map((n) => {
          if (n.ownerOnly && user?.role !== "tenant_owner") return null;
          return (
          <NavLink
            key={n.testid}
            to={n.to}
            end={n.to === "/app"}
            data-testid={n.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-3 rounded-md text-sm transition-colors ${
                isActive ? "text-white" : "hover:bg-[var(--surface-muted)] text-[var(--text-main)]"
              }`
            }
            style={({ isActive }) => (isActive ? { backgroundColor: "var(--primary)" } : {})}
          >
            <n.icon size={18} strokeWidth={2.25} />
            <span className="font-medium">{n.label}</span>
          </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t" style={{ borderColor: "var(--border-soft)" }}>
        <div className="px-3 py-2 text-sm">
          <div className="flex items-center justify-between gap-2">
            <div className="font-medium truncate min-w-0" data-testid="sidebar-user-name">{user?.name}</div>
            <Link
              to="/app/settings"
              data-testid="sidebar-settings"
              className="shrink-0 w-8 h-8 rounded-md grid place-items-center hover:bg-[var(--surface-muted)]"
              title="Settings"
              style={settingsActive ? { background: "var(--primary)", color: "white" } : { color: "var(--text-muted)" }}
            >
              <SettingsIcon size={18} />
            </Link>
          </div>
          <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>{user?.email}</div>
        </div>
        <button
          data-testid="sidebar-logout"
          onClick={() => { logout(); nav_("/"); }}
          className="w-full mt-2 flex items-center gap-2 px-3 py-3 rounded-md text-sm hover:bg-[var(--surface-muted)]"
        >
          <LogOut size={18} /> Sign out
        </button>
      </div>
    </aside>
  );
}
